import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import queryString from "query-string";

import { QUERY_WIDTHS, TABLE_SUBMISSIONS_COLUMNS } from "@utils/index";
import { SubmissionModel, TableColumnModel } from "@interfaces/index";
import { useCommonStore, useUserStore } from "@stores/index";
import { useSubmissionGetAll } from "@providers/index";
import { useBusinessType, useNotify } from "@hooks/index";
import {
  PageContentContainer,
  PageContentTitle,
  Table,
  TableHead,
  TableBody,
  TrNoItems,
  TableRowSubmission,
  Pagination,
  RangePicker,
  BusinessSelector,
  SubmissionsStatusFilter,
  DesktopPageTitle,
  TutorialButton,
} from "@components/index";

export const SubmissionsTable = ({
  isPrivateUse,
  activeTab,
}: {
  isPrivateUse?: boolean;
  activeTab?: string;
}) => {
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const location = useLocation();
  const navigate = useNavigate();
  const notify = useNotify();
  const setState = useCommonStore(({ setState }) => setState);

  const puEnabled = useUserStore(({ puEnabled }) => puEnabled);
  const disabledFeatures = useUserStore(
    ({ disabledFeatures }) => disabledFeatures,
  );
  const businesses = useUserStore(({ businesses }) => businesses);
  const defaultBusiness = useUserStore(({ business }) => business);
  const [businessId, setBusinessId] = useState("");
  const businessType = useBusinessType(businessId);

  const [statusId, setStatusId] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [submissions, setSubmissions] = useState<SubmissionModel[]>();

  const [selectedPage, setSelectedPage] = useState(1);
  const [countSubmissions, setCountSubmissions] = useState(0);
  const [perPage, setPerPage] = useState(5);

  const [redirectNeeds, setRedirectNeeds] = useState(false);

  const availableTutorialNames = useMemo(() => {
    const arr = [];
    if (submissions?.length) {
      arr.push("submissionViewTutorial");
      arr.push("submissionDetailsViewTutorial");
      if (isPrivateUse && submissions?.length) {
        arr.push("privateUseCreateTutorial");
      }
    }
    return arr;
  }, [isPrivateUse, submissions]);

  useEffect(() => {
    const params = queryString.parse(location.search);
    setBusinessId(
      (params?.businessId as string) ||
        defaultBusiness?.uniqueId ||
        businesses[0]?.uniqueId ||
        "",
    );
    setStatusId((params?.statusId as string) || "");
    setSelectedPage(parseInt((params?.page || "1").toString()));
    setStartDate((params?.startDate as string) || "");
    setEndDate((params?.endDate as string) || "");
  }, [location, defaultBusiness, businesses]);

  const {
    isPending: isPendingGetSubmissions,
    isSuccess: isSuccessGetSubmissions,
    data: dataGetSubmissions,
    isError: isErrorGetSubmissions,
    error: errorGetSubmissions,
  } = useSubmissionGetAll({
    businessId,
    status: statusId,
    startDate,
    endDate,
    page: selectedPage,
  });

  useEffect(() => {
    if (isSuccessGetSubmissions && dataGetSubmissions) {
      setSubmissions(dataGetSubmissions.submissions);
      setCountSubmissions(dataGetSubmissions.pagination.total);
      setPerPage(dataGetSubmissions.pagination.perPage);
    }
  }, [isSuccessGetSubmissions, dataGetSubmissions]);

  useEffect(() => {
    if (isErrorGetSubmissions && errorGetSubmissions) {
      notify("error", errorGetSubmissions.message);
    }
  }, [isErrorGetSubmissions, errorGetSubmissions, notify]);

  useEffect(() => {
    setState({ isLoading: isPendingGetSubmissions });
  }, [isPendingGetSubmissions, setState]);

  useEffect(() => {
    if (!redirectNeeds) {
      return;
    }
    setRedirectNeeds(false);
    const params = queryString.stringify(
      {
        businessId,
        page: selectedPage,
        statusId,
        startDate,
        endDate,
      },
      { skipEmptyString: true },
    );
    navigate(`?${params}`);
  }, [
    redirectNeeds,
    navigate,
    businessId,
    selectedPage,
    statusId,
    startDate,
    endDate,
  ]);

  const handleSelectBusiness = (businessId: string) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setBusinessId(businessId);
  };

  const handleSelectPage = (page: number) => {
    setRedirectNeeds(true);
    setSelectedPage(page);
  };

  const handleSelectStatus = (statusId: string) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setStatusId(statusId);
  };

  const handleSetStartDate = (startDate: string) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setStartDate(startDate);
  };

  const handleSetEndDate = (endDate: string) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setEndDate(endDate);
  };

  const formatColumnTitles = (columns: TableColumnModel[]) =>
    columns
      .map((column) => {
        if (
          businessType !== "landlord" ||
          column.title !== "Business Expenses"
        ) {
          return column;
        } else {
          return {
            ...column,
            title: "Expenses",
          };
        }
      })
      .filter(
        (column) =>
          (puEnabled && !disabledFeatures.includes("Private Use")) ||
          column.title !== "Private",
      );

  let TABLE_SUBMISSIONS_COLUMNS_filtered = TABLE_SUBMISSIONS_COLUMNS;
  if (
    !puEnabled ||
    disabledFeatures.includes("Private Use") ||
    (puEnabled && businessType === "landlord")
  ) {
    TABLE_SUBMISSIONS_COLUMNS_filtered = TABLE_SUBMISSIONS_COLUMNS.filter(
      (col) => col.title !== "Private",
    );
  }

  return (
    <div className="md:pt-6">
      {!isMobile && (
        <DesktopPageTitle
          title="Submissions"
          tutorialNames={availableTutorialNames}
          faqCategory={["Submissions"]}
        />
      )}
      <div className="flex flex-col justify-between gap-y-4 px-4 pt-4 md:flex-row md:px-10 md:pt-6">
        <BusinessSelector
          height="small"
          width="w-full md:w-64"
          value={businessId}
          onSetBusiness={handleSelectBusiness}
          dataTestid="submissions_select_businessId"
        />
        <RangePicker
          startDate={startDate}
          endDate={endDate}
          prefix="submissionsTable"
          onSetStartDate={handleSetStartDate}
          onSetEndDate={handleSetEndDate}
          showPeriods={true}
          dataTestid="filter_dateRangeSelector"
        />
      </div>
      <PageContentContainer>
        <div className="flex flex-col justify-between pb-2 md:flex-row md:pb-0">
          <div className="flex items-center justify-between">
            <PageContentTitle title="Submissions" />
            {isMobile && (
              <TutorialButton
                variant="dark"
                tutorialNames={availableTutorialNames}
                faqCategory={["Submissions"]}
              />
            )}
            <div className="text-button font-normal text-black-54 md:hidden">
              Filter By:
            </div>
          </div>
          <div className="flex items-center gap-x-4">
            <div className="hidden text-button font-normal text-black-54 md:block">
              Filter By:
            </div>
            <div className="mt-2 flex w-full flex-col gap-4 md:mt-0 md:w-auto md:flex-row">
              <SubmissionsStatusFilter
                height="small"
                width="w-full md:w-52"
                value={statusId}
                onSetStatus={handleSelectStatus}
                dataTestid="filter_filtersSelector"
              />
            </div>
          </div>
        </div>
        <Table className="pb-4 md:pb-0" dataTestid="table_submissionsTable">
          {!isMobile && (
            <TableHead
              columns={formatColumnTitles(TABLE_SUBMISSIONS_COLUMNS_filtered)}
              dataTestid="thead_submissionsTable"
            />
          )}
          <TableBody dataTestid="tbody_submissionsTable">
            {submissions?.length ? (
              submissions.map((submission) => (
                <TableRowSubmission
                  key={submission.uniqueId}
                  submission={submission}
                  puEnabled={
                    puEnabled &&
                    !disabledFeatures.includes("Private Use") &&
                    businessType !== "landlord"
                  }
                  businessType={businessType}
                  disabled={isPrivateUse && businessType === "landlord"}
                  onDetails={() =>
                    navigate(
                      `/${isPrivateUse ? "privateUse" : "submissions"}/details/${submission.uniqueId}${activeTab ? `?activeTab=${activeTab}` : ""}`,
                    )
                  }
                />
              ))
            ) : (
              <TrNoItems title="No submissions found" />
            )}
          </TableBody>
        </Table>
        {countSubmissions > 0 && (
          <Pagination
            count={countSubmissions}
            perPage={perPage}
            currentPage={selectedPage}
            onSelect={handleSelectPage}
          />
        )}
      </PageContentContainer>
    </div>
  );
};
